import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Theme,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import MergeTypeOutlinedIcon from "@mui/icons-material/MergeTypeOutlined";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { Colors, darkSurfaces } from "../../theme";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsAdmin } from "../../auth/useIsAdmin";
import { useRoles } from "../../auth/useRoles";
import { canBulkSyncWarehouse } from "../../auth/permissions";
import { useIsMobileLayout } from "../useIsMobileLayout";

const navigations = [
  "home",
  "tradingcounterparties",
  "brokers",
  "mergers",
  "countries",
  "counterpartylookup"
];

// Warehouse sync writes credit and KYC values across every counterparty and broker, so the tab is
// only offered to the roles that own those fields. The page and its menubar check the same helper.
const warehouseSyncNav = "warehousesync";

const railBackground = (t: Theme) =>
  t.palette.mode === "dark" ? darkSurfaces.chrome : t.palette.primary.dark;

// In dark mode the selected item also gets a faint orange wash, so the one accent in the frame
// marks where you are.
const selectedWash = (t: Theme) =>
  t.palette.mode === "dark"
    ? "rgba(255, 140, 61, 0.10)"
    : "rgba(255, 255, 255, 0.08)";

type Props = {
  /** Below md the rail is hidden and the nav opens over the content as a drawer instead. */
  mobileOpen: boolean;
  onMobileClose: () => void;
};

export const NavBar: FC<Props> = ({ mobileOpen, onMobileClose }) => {
  const isMobile = useIsMobileLayout();
  const location = useLocation();
  const locationWithoutSlash = location.pathname.split("/")[1];
  const [value, setValue] = useState(
    locationWithoutSlash.length ? locationWithoutSlash : "home"
  );
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const roles = useRoles();
  const bottomNavigations = isAdmin ? ["admin"] : [];

  const navWithoutNull = navigations.filter((nav) => nav !== null);

  const topNavigations = canBulkSyncWarehouse(roles)
    ? [...navWithoutNull, warehouseSyncNav]
    : navWithoutNull;

  useEffect(() => {
    setValue(
      locationWithoutSlash === "" ? "home" : (locationWithoutSlash ?? "home")
    );
  }, [locationWithoutSlash]);

  const onValChange = (
    _e: React.SyntheticEvent<Element, Event>,
    value: string
  ) => {
    setValue(value);
    navigate(`/${value}`);
  };

  const renderTab = (nav: string) => (
    <Tab
      key={nav}
      value={nav}
      label={
        <Tooltip title={getTitle(nav)} placement="right" arrow>
          {getIcon(nav)}
        </Tooltip>
      }
      sx={{
        minHeight: 56,
        width: 40,
        maxWidth: 48,
        minWidth: 48
      }}
    />
  );

  const topValue = topNavigations.includes(value) ? value : false;
  const bottomValue = bottomNavigations.includes(value) ? value : false;

  if (isMobile) {
    const renderItem = (nav: string) => (
      <ListItemButton
        key={nav}
        selected={nav === value}
        onClick={() => {
          onMobileClose();
          setValue(nav);
          navigate(`/${nav}`);
        }}
        sx={{
          minHeight: 48,
          borderLeft: "3px solid transparent",
          "&.Mui-selected": {
            borderLeftColor: Colors.MFT_ORANGE,
            backgroundColor: selectedWash
          },
          "&.Mui-selected svg": { fill: Colors.MFT_ORANGE }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>{getIcon(nav)}</ListItemIcon>
        <ListItemText
          primary={getTitle(nav)}
          slotProps={{ primary: { color: "MFT_OFF_WHITE" } }}
        />
      </ListItemButton>
    );

    return (
      <Drawer
        open={mobileOpen}
        onClose={onMobileClose}
        slotProps={{
          paper: {
            sx: {
              width: 260,
              maxWidth: "80vw",
              backgroundColor: railBackground,
              backgroundImage: "none",
              display: "flex",
              flexDirection: "column"
            }
          }
        }}
      >
        <Toolbar>
          <Typography variant="subtitle1" color="MFT_OFF_WHITE">
            Market Access Hub
          </Typography>
        </Toolbar>
        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />
        <List component="nav">{topNavigations.map(renderItem)}</List>
        <Box sx={{ flexGrow: 1 }} />
        {bottomNavigations.length > 0 && (
          <>
            <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />
            <List component="nav">{bottomNavigations.map(renderItem)}</List>
          </>
        )}
      </Drawer>
    );
  }

  return (
    <Box
      className="ma-nav-rail"
      gap={2}
      sx={{
        backgroundColor: railBackground,
        width: 48,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        zIndex: 9,
        boxShadow: (t) =>
          t.palette.mode === "dark"
            ? "none"
            : "2px 0px 8px rgba(0, 0, 0, 0.3), inset -1px 0px 0px rgba(0, 0, 0, 0.1)",
        borderRight: (t) =>
          t.palette.mode === "dark"
            ? `1px solid ${darkSurfaces.border}`
            : "none",
        "& .Mui-selected svg": {
          fill: Colors.MFT_ORANGE
        },
        "& .MuiTab-root.Mui-selected": {
          backgroundColor: (t: Theme) =>
            t.palette.mode === "dark" ? selectedWash(t) : undefined
        }
      }}
    >
      <Tabs
        orientation="vertical"
        value={topValue}
        indicatorColor={"secondary"}
        onChange={onValChange}
        sx={{
          maxWidth: 48,
          minWidth: 48
        }}
      >
        {topNavigations.map(renderTab)}
      </Tabs>
      <Box sx={{ flexGrow: 1 }} />
      <Tabs
        orientation="vertical"
        value={bottomValue}
        indicatorColor={"secondary"}
        onChange={onValChange}
        sx={{
          maxWidth: 48,
          minWidth: 48
        }}
      >
        {bottomNavigations.map(renderTab)}
      </Tabs>
    </Box>
  );
};

const getIcon = (nav: string) => {
  switch (nav) {
    case "home":
      return <HomeOutlinedIcon sx={{ color: (t) => t.palette.MFT_CREAM }} />;
    case "tradingcounterparties":
      return (
        <HandshakeOutlinedIcon sx={{ color: (t) => t.palette.MFT_CREAM }} />
      );
    case "brokers":
      return (
        <AccountBalanceOutlinedIcon
          sx={{ color: (t) => t.palette.MFT_CREAM }}
        />
      );
    case "mergers":
      return (
        <MergeTypeOutlinedIcon sx={{ color: (t) => t.palette.MFT_CREAM }} />
      );
    case "countries":
      return <PublicOutlinedIcon sx={{ color: (t) => t.palette.MFT_CREAM }} />;
    case "counterpartylookup":
      return (
        <ManageSearchOutlinedIcon sx={{ color: (t) => t.palette.MFT_CREAM }} />
      );
    case "warehousesync":
      return <SyncAltOutlinedIcon sx={{ color: (t) => t.palette.MFT_CREAM }} />;
    case "admin":
      return (
        <AdminPanelSettingsOutlinedIcon
          sx={{ color: (t) => t.palette.MFT_CREAM }}
        />
      );
    default:
      return <></>;
  }
};

const getTitle = (nav: string) => {
  switch (nav) {
    case "home":
      return "Home";
    case "tradingcounterparties":
      return "Trading counterparties";
    case "brokers":
      return "Brokers";
    case "mergers":
      return "Mergers";
    case "countries":
      return "Country additions";
    case "counterpartylookup":
      return "Counterparty lookup";
    case "warehousesync":
      return "Warehouse sync";
    case "admin":
      return "Admin";
    default:
      return "";
  }
};
